import React, { useCallback } from 'react';
import { Form } from 'react-bootstrap';
import type { TextDestCondition } from 'dorian-shared/types/branch/HubOption';
import { CustomSelect } from '../../../../../CustomSelect';
import { parseGotoBranchId } from '../../shared/hubUtils';
import { BranchSelectItem } from '../../shared/types';
import {
  HUB_CARD_TEXT_MAX_LENGTH,
  HUB_CARD_TEXT_WARN_LENGTH,
  HUB_CONTINUE_MAX_LENGTH,
} from '../../hubStepDefaults';

type TextDestResultRowProps = {
  condition: TextDestCondition;
  branches: BranchSelectItem[];
  currentBranchId: number | null;
  isDisabled: boolean;
  isContinue: boolean;
  onChange: (updated: TextDestCondition) => void;
};

export function TextDestResultRow({
  condition,
  branches,
  currentBranchId,
  isDisabled,
  isContinue,
  onChange,
}: TextDestResultRowProps) {
  const textMaxLength = isContinue ? HUB_CONTINUE_MAX_LENGTH : HUB_CARD_TEXT_MAX_LENGTH;
  const textWarnLength = isContinue ? HUB_CONTINUE_MAX_LENGTH : HUB_CARD_TEXT_WARN_LENGTH;
  const textLength = condition.resultText?.length ?? 0;
  const handleTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      onChange({ ...condition, resultText: val || null });
    },
    [condition, onChange],
  );

  const handleGotoChange = useCallback(
    (raw: unknown) => {
      onChange({ ...condition, resultGotoBranchId: parseGotoBranchId(raw) });
    },
    [condition, onChange],
  );

  return (
    <div className="text-dest-condition-card__result" data-testid="text-dest-result-row">
      <div className="text-dest-condition-card__result-field">
        <span className="text-dest-condition-card__result-field-label">
          <span className="text-dest-condition-card__result-arrow">→</span> Show text
        </span>
        <div className="text-dest-condition-card__text-input-wrap">
          <Form.Control
            size="sm"
            type="text"
            className={`text-dest-condition-card__text-input${textLength > textWarnLength ? ' text-warn' : ''}`}
            placeholder="Override title"
            value={condition.resultText ?? ''}
            onChange={handleTextChange}
            disabled={isDisabled}
            maxLength={textMaxLength}
            data-testid="result-text-input"
          />
          <Form.Text className="char-limit-info-box" style={{ top: '-8px', bottom: 'auto' }}>
            {textWarnLength - textLength}
          </Form.Text>
        </div>
      </div>

      <div className="text-dest-condition-card__result-field">
        <span className="text-dest-condition-card__result-field-label">
          <span className="text-dest-condition-card__result-arrow">→</span> Go to node
        </span>
        <div className="text-dest-condition-card__goto-select">
          <CustomSelect
            disabled={isDisabled}
            value={condition.resultGotoBranchId ?? ''}
            onChange={handleGotoChange}
            placeholder="—"
            args={[
              { id: -1, title: '[Create New Node]' },
              ...branches.filter((b) => b.id !== currentBranchId),
            ]}
            data={currentBranchId}
          />
        </div>
      </div>
    </div>
  );
}
